import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import {
  PerksLinkRequestStatusEnum,
  useGetAllPerksLinksForChamberQuery,
  useRequestPerksLinkMutation,
  PerksLinkReturn,
  useDeletePerksLinkRequestMutation,
  PerksLinkDeletionReasonEnum,
  useAcceptPerksLinkRequestMutation,
  GetAllPerksLinksForChamberDocument,
} from "../../../graphql/types";
import {
  TextField,
  makeStyles,
  Button,
  Modal,
  CircularProgress,
  Chip,
  useMediaQuery,
  Switch,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import CancelIcon from "../../../images/ooui_cancel.png";
import theme from "../../../themes";
import Loader from "../../common/Loader/Loader";
import { toast, ToastContainer } from "react-toastify";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import HoverToolTip from "../../common/utilities/HoverToolTip";
import { getLocalTimestamp } from "../Root/RootAdmin";
import { useEnumStrings } from "../../enumStrings";
import ActionDialog from "../../common/ActionDialog";
import InformationIcon from "../../../images/information-icon.png";

const useStyles = makeStyles(() => ({
  companyInputWithToolTipContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    [theme.breakpoints.up("sm")]: {
      width: "550px",
    },
  },
  searchContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "10px",
    marginTop: "10px",
    zIndex: 9,
  },
  searchBar: {
    zIndex: 9,
    outline: "none",
    background: "#FFFFFF",
    borderRadius: "15px",
    paddingLeft: "10px",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "41px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "450px",
      height: "41px",
    },
  },
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    boxSizing: "border-box" as const,
    borderRadius: "20px",
    textAlign: "center" as const,
    width: "550px",
    maxWidth: "90%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column" as const,
    backgroundColor: "#FFFFFF",
    border: "1px solid #666666",
    padding: "30px",
    position: "relative" as const,
    outline: "none",
  },
  modalCloseButton: {
    position: "absolute" as const,
    right: "16px",
    top: "16px",
    width: "30px",
    height: "30px",
    borderRadius: "100px",
    backgroundColor: "#ED1C24",
    color: "#FFFFFF",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { backgroundColor: "#D41920" },
  },
  modalCheckCircle: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    backgroundColor: "#00B621",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "12px",
  },
  modalTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "110%",
    color: "#000000",
    marginBottom: "12px",
  },
  modalBody: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "180%",
    color: "#000000",
    textAlign: "center" as const,
    maxWidth: "450px",
  },
  modalSendButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 60px",
    backgroundColor: "#00B621",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    marginTop: "16px",
    "&:hover": { backgroundColor: "#009A1B" },
  },
  modalSendButtonText: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "150%",
    letterSpacing: "-0.04em",
    color: "#FFFFFF",
    textTransform: "none" as const,
  },
  modalCancelCircle: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    backgroundColor: "#ED1C24",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "12px",
  },
  modalCancelButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 60px",
    backgroundColor: "#ED1C24",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    marginTop: "16px",
    "&:hover": { backgroundColor: "#D41920" },
  },
  modalCancelButtonText: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "150%",
    letterSpacing: "-0.04em",
    color: "#FFFFFF",
    textTransform: "none" as const,
  },
  modalButton: {
    margin: "10px",
    marginTop: "20px",
    width: "230px",
    backgroundColor: "#E1A731",
    color: "#e9e9e9",
    fontWeight: "bold",
    height: "40px",
  },
  notificationRadioGroup: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginBottom: "10px",
    alignItems: "center",
    paddingLeft: "100px",
  },
  radio: {
    color: "#E1A731!important",
  },
  chamberList: {
    height: "700px",
    "@media (max-height:1030px)": {
      height: "550px",
    },
    "@media (max-height:900px)": {
      height: "450px",
    },
    "@media (max-height:770px)": {
      height: "350px",
    },
    "@media (max-height:650px)": {
      height: "200px",
    },
  },
}));

export default function PerksLinkChamberList() {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [fullList, setFullList] = useState<
    Pick<
      PerksLinkReturn & {
        linked: boolean;
        linkPendingAsRequestor: boolean;
        linkPendingAsRequestee: boolean;
        perksLinkRequestId: string;
        requestedAtUtcMilli: number;
        linkedAtTimeUtcMilli: number;
      },
      | "chamberId"
      | "chamberName"
      | "chamberContactEmail"
      | "linked"
      | "linkPendingAsRequestor"
      | "linkPendingAsRequestee"
      | "perksLinkRequestId"
      | "requestedAtUtcMilli"
      | "chamberContactNumber"
      | "linkedAtTimeUtcMilli"
    >[]
  >([]);

  const [isLoading, setIsLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const handleSearchChange = useCallback((e: any) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  }, []);

  const userChamberInfo = useGetChamberFromUserIdInAuth();

  const { data: allPerksLinkChamberData } = useGetAllPerksLinksForChamberQuery({
    fetchPolicy: "network-only",
  });

  const updateListOfChambers = useCallback(() => {
    let baseChamberArr = [];
    for (const chamber of allPerksLinkChamberData?.getAllPerksLinksForChamber ||
      []) {
      baseChamberArr.push({
        chamberId: chamber.chamberId,
        chamberName: chamber.chamberName,
        linked:
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.status ===
          PerksLinkRequestStatusEnum.Accepted,
        chamberContactEmail: chamber.chamberContactEmail,
        linkPendingAsRequestor:
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.status ===
            PerksLinkRequestStatusEnum.Pending &&
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.requestorChamberId ===
            userChamberInfo?.id,
        linkPendingAsRequestee:
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.status ===
            PerksLinkRequestStatusEnum.Pending &&
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.requesteeChamberId ===
            userChamberInfo?.id,
        perksLinkRequestId:
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.id || "",
        requestedAtUtcMilli:
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.createdAtUtcMilli || 0,
        chamberContactNumber: chamber.chamberContactNumber,
        linkedAtTimeUtcMilli:
          chamber.pendingOrAcceptedPerksLinkRequestInfo?.linkedAtTimeUtcMilli ||
          0,
      });
    }

    const linkedChambers = baseChamberArr
      .filter((chamber) => chamber.linked)
      .sort((a, b) => a.chamberName.localeCompare(b.chamberName));

    const linkPendingAsRequestorChambers = baseChamberArr
      .filter((chamber) => chamber.linkPendingAsRequestor)
      .sort((a, b) => a.chamberName.localeCompare(b.chamberName));

    // const linkPendingAsRequesteeChambers = baseChamberArr
    //   .filter((chamber) => chamber.linkPendingAsRequestee)
    //   .sort((a, b) => a.chamberName.localeCompare(b.chamberName));

    const nonLinkedChambers = baseChamberArr
      .filter((chamber) => !chamber.linked && !chamber.linkPendingAsRequestor)
      .sort((a, b) => a.chamberName.localeCompare(b.chamberName));

    const allChamberSelectableArray = [
      ...linkedChambers,
      ...linkPendingAsRequestorChambers,
      // ...linkPendingAsRequesteeChambers,
      ...nonLinkedChambers,
    ];

    setFullList(allChamberSelectableArray || []);
  }, [
    allPerksLinkChamberData?.getAllPerksLinksForChamber,
    userChamberInfo?.id,
  ]);

  useEffect(() => {
    if (
      isLoading &&
      allPerksLinkChamberData?.getAllPerksLinksForChamber &&
      userChamberInfo
    ) {
      updateListOfChambers();
      setIsLoading(false);
    }
  }, [
    isLoading,
    allPerksLinkChamberData?.getAllPerksLinksForChamber,
    userChamberInfo,
    updateListOfChambers,
  ]);

  useEffect(() => {
    updateListOfChambers();
  }, [updateListOfChambers]);

  const searchedItems = useMemo(() => {
    return fullList.filter((chamber) => {
      return chamber.chamberName.toLowerCase().match(searchInput.toLowerCase());
    });
  }, [fullList, searchInput]);

  const [linkConfirmModalOpen, setLinkConfirmModalOpen] = useState(false);
  const [requestSuccessModalOpen, setRequestSuccessModalOpen] = useState(false);
  const [requestedLinkChamberName, setRequestedLinkChamberName] = useState("");
  const [requestedLinkChamberEmail] = useState("");
  const [requestedLinkChamberId, setRequestedLinkChamberId] = useState("");

  // Sand Bay-only preview toggle: show what a pending request would look like
  // if the requestee accepts. Used for internal testing — do not ship to prod.
  const isSandBayTestChamber = (userChamberInfo?.name || "")
    .toLowerCase()
    .includes("sand bay");
  const [previewAcceptedIds, setPreviewAcceptedIds] = useState<Record<string, boolean>>({});

  const [requestLinkMutation] = useRequestPerksLinkMutation();

  const [sendLoading, setSendLoading] = useState(false);

  const requestLink = useCallback(
    async ({
      requestedLinkChamberIdManual,
    }: {
      requestedLinkChamberIdManual: string;
    }) => {
      setSendLoading(true);

      await requestLinkMutation({
        variables: {
          requestingLinkToChamberId: requestedLinkChamberIdManual,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: GetAllPerksLinksForChamberDocument },
          "getAllPerksLinksForChamber",
        ],
      });

      setSendLoading(false);
      setLinkConfirmModalOpen(false);
      setRequestSuccessModalOpen(true);
      setTimeout(() => setRequestSuccessModalOpen(false), 3000);
    },
    [requestLinkMutation]
  );

  const [cancelLinkRequestModalOpen, setCancelLinkRequestModalOpen] =
    useState(false);
  const [cancelSuccessModalOpen, setCancelSuccessModalOpen] = useState(false);
  const [denyLinkRequestModalOpen, setDenyLinkRequestModalOpen] =
    useState(false);
  const [unlinkRequestModalOpen, setUnlinkRequestModalOpen] = useState(false);

  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);
  const [perksLinkRequestId, setPerksLinkRequestId] = useState("");

  const [deletePerksLinkRequestMutation] = useDeletePerksLinkRequestMutation();

  const cancelRequest = useCallback(
    async ({
      deletionReason,
    }: {
      deletionReason: PerksLinkDeletionReasonEnum;
    }) => {
      setDeleteRequestLoading(true);

      await deletePerksLinkRequestMutation({
        variables: {
          requestId: perksLinkRequestId,
          deletionReason,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: GetAllPerksLinksForChamberDocument },
          "getAllPerksLinksForChamber",
        ],
      });

      setDeleteRequestLoading(false);
      setCancelLinkRequestModalOpen(false);
      setDenyLinkRequestModalOpen(false);
      setUnlinkRequestModalOpen(false);

      if (deletionReason === PerksLinkDeletionReasonEnum.Cancelled) {
        setCancelSuccessModalOpen(true);
        setTimeout(() => setCancelSuccessModalOpen(false), 3000);
      } else {
        toast.success(
          `${
            deletionReason === PerksLinkDeletionReasonEnum.Denied
              ? enumStrings.perksLink.requestDenied
              : enumStrings.perksLink.unlinked
          } ${enumStrings.perksLink.successfully}`
        );
      }
    },
    [
      deletePerksLinkRequestMutation,
      enumStrings.perksLink.requestCancelled,
      enumStrings.perksLink.requestDenied,
      enumStrings.perksLink.successfully,
      enumStrings.perksLink.unlinked,
      perksLinkRequestId,
    ]
  );

  const [acceptRequestModalOpen, setAcceptRequestModalOpen] = useState(false);
  const [acceptRequestLoading, setAcceptRequestLoading] = useState(false);
  const [acceptPerksLinkRequestMutation] = useAcceptPerksLinkRequestMutation();

  const acceptRequest = useCallback(async () => {
    setAcceptRequestLoading(true);

    await acceptPerksLinkRequestMutation({
      variables: {
        requestId: perksLinkRequestId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        { query: GetAllPerksLinksForChamberDocument },
        "getAllPerksLinksForChamber",
      ],
    });

    toast.success(enumStrings.perksLink.requestAccepted);
    setAcceptRequestLoading(false);
    setAcceptRequestModalOpen(false);
  }, [
    acceptPerksLinkRequestMutation,
    enumStrings.perksLink.requestAccepted,
    perksLinkRequestId,
  ]);

  const customList = useMemo(() => {
    return (
      <Card>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          title={
            <div className={classes.searchContainer}>
              <TextField
                style={{
                  borderBottom: "none",
                  border: "1px solid #9E9E9E",
                }}
                type="text"
                placeholder={enumStrings.chamberList.placeholder}
                onChange={handleSearchChange}
                value={searchInput}
                className={classes.searchBar}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: <SearchIcon style={{ color: "#AEAEAE" }} />,
                }}
              />
            </div>
          }
        />
        <Divider />
        <List
          sx={{
            bgcolor: "background.paper",
            overflow: "auto",
          }}
          className={classes.chamberList}
          dense
          component="div"
          role="list"
        >
          {searchedItems.length === 0 && (
            <ListItem
              role="listitem"
              style={{
                paddingTop: 10,
                paddingBottom: 10,
                cursor: "unset",
              }}
            >
              <ListItemText primary={enumStrings.chamberList.primary} />
            </ListItem>
          )}
          {searchedItems.map((value) => {
            const labelId = `transfer-list-all-item-${value.chamberId}-label`;

            return (
              <ListItem
                key={value.chamberId}
                role="listitem"
                divider
                button
                style={{
                  paddingTop: 10,
                  paddingBottom: 10,
                  cursor: "unset",
                }}
              >
                <ListItemText id={labelId} primary={value.chamberName} />
                <ListItemIcon>
                  {value.linked ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <span style={{
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "#ED1C24",
                        textTransform: "uppercase",
                        marginBottom: "8px",
                      }}>
                        {enumStrings.perksLink.activated}
                      </span>
                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: "#ED1C24",
                          color: "#FFFFFF",
                          fontSize: "11px",
                          fontWeight: "bold",
                          borderRadius: "10px",
                          width: isMobile ? "auto" : "210px",
                          minWidth: isMobile ? "100px" : undefined,
                          height: "30px",
                          textTransform: "none",
                          whiteSpace: "normal",
                          padding: "4px 12px",
                        }}
                        disabled={sendLoading}
                        onClick={() => {
                          setUnlinkRequestModalOpen(() => {
                            setRequestedLinkChamberName(value.chamberName);
                            setPerksLinkRequestId(value.perksLinkRequestId);
                            return true;
                          });
                        }}
                      >
                        DEACTIVATE
                      </Button>
                    </div>
                  ) : value.linkPendingAsRequestor ? (
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
                      {isSandBayTestChamber && (
                        <div
                          style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <span style={{ fontSize: "10px", fontWeight: 600, color: "#666" }}>
                            Preview accepted
                          </span>
                          <Switch
                            size="small"
                            color="primary"
                            checked={!!previewAcceptedIds[value.chamberId]}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              setPreviewAcceptedIds((prev) => ({
                                ...prev,
                                [value.chamberId]: e.target.checked,
                              }));
                            }}
                          />
                        </div>
                      )}
                      {isSandBayTestChamber && previewAcceptedIds[value.chamberId] ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <span style={{
                            fontFamily: "DM Sans, sans-serif",
                            fontWeight: 700,
                            fontSize: "13px",
                            color: "#00B621",
                            textTransform: "uppercase",
                            marginBottom: "8px",
                            textAlign: "center",
                          }}>
                            {enumStrings.perksLink.activated}
                          </span>
                          <Button
                            variant="contained"
                            style={{
                              backgroundColor: "#ED1C24",
                              color: "#FFFFFF",
                              fontSize: "11px",
                              fontWeight: "bold",
                              borderRadius: "10px",
                              width: isMobile ? "auto" : "210px",
                              minWidth: isMobile ? "100px" : undefined,
                              height: "30px",
                              textTransform: "none",
                              whiteSpace: "normal",
                              padding: "4px 12px",
                            }}
                            onClick={() => {
                              setUnlinkRequestModalOpen(() => {
                                setRequestedLinkChamberName(value.chamberName);
                                setPerksLinkRequestId(value.perksLinkRequestId);
                                return true;
                              });
                            }}
                          >
                            DEACTIVATE
                          </Button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                          <Button
                            variant="contained"
                            style={{
                              backgroundColor: "#00B621",
                              color: "#FFFFFF",
                              fontSize: "11px",
                              fontWeight: "bold",
                              borderRadius: "10px",
                              width: isMobile ? "auto" : "210px",
                              minWidth: isMobile ? "100px" : undefined,
                              height: "30px",
                              textTransform: "none",
                              whiteSpace: "normal",
                              padding: "4px 12px",
                            }}
                            disabled
                          >
                            {enumStrings.perksLink.linkRequestSent}
                          </Button>
                          <div style={{ height: "8px" }} />
                          <Button
                            variant="contained"
                            style={{
                              backgroundColor: "#ED1C24",
                              color: "#FFFFFF",
                              fontSize: "11px",
                              fontWeight: "bold",
                              borderRadius: "10px",
                              width: isMobile ? "auto" : "210px",
                              minWidth: isMobile ? "100px" : undefined,
                              height: "30px",
                              textTransform: "none",
                              whiteSpace: "normal",
                              padding: "4px 12px",
                            }}
                            onClick={() => {
                              setCancelLinkRequestModalOpen(() => {
                                setRequestedLinkChamberName(value.chamberName);
                                setPerksLinkRequestId(value.perksLinkRequestId);
                                return true;
                              });
                            }}
                          >
                            {enumStrings.chamberList.cancelRequest}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      size="large"
                      color="primary"
                      variant="contained"
                      style={{
                        backgroundColor: "#E1A731",
                        fontSize: "11px",
                        fontWeight: "bold",
                        borderRadius: "10px",
                        width: isMobile ? "auto" : "210px",
                        minWidth: isMobile ? "100px" : undefined,
                        height: "30px",
                        whiteSpace: "normal",
                        padding: "4px 12px",
                      }}
                      disabled={sendLoading}
                      onClick={() => {
                        setLinkConfirmModalOpen(() => {
                          setRequestedLinkChamberId(value.chamberId);
                          setRequestedLinkChamberName(value.chamberName);
                          return true;
                        });
                      }}
                    >
                      {requestedLinkChamberId === value.chamberId &&
                      sendLoading ? (
                        <CircularProgress
                          style={{ color: "white" }}
                          size={28}
                        />
                      ) : (
                        enumStrings.perksLink.requestLink
                      )}
                    </Button>
                  )}
                </ListItemIcon>
              </ListItem>
            );
          })}
        </List>
      </Card>
    );
  }, [
    classes.chamberList,
    classes.searchBar,
    classes.searchContainer,
    enumStrings.chamberList.cancelRequest,
    enumStrings.chamberList.description,
    enumStrings.chamberList.description2,
    enumStrings.chamberList.description3,
    enumStrings.chamberList.placeholder,
    enumStrings.chamberList.primary,
    enumStrings.perksLink.and,
    enumStrings.perksLink.at,
    enumStrings.perksLink.linkRequestSent,
    enumStrings.perksLink.linked,
    enumStrings.perksLink.linkedPerks,
    enumStrings.perksLink.on,
    enumStrings.perksLink.or,
    enumStrings.perksLink.requestLink,
    enumStrings.perksLink.unlink,
    handleSearchChange,
    isMobile,
    requestLink,
    requestedLinkChamberId,
    searchInput,
    searchedItems,
    sendLoading,
    userChamberInfo?.name,
  ]);

  if (!allPerksLinkChamberData?.getAllPerksLinksForChamber || isLoading) {
    return (
      <div style={{ marginTop: "46px" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
      />
      {linkConfirmModalOpen && (
        <Modal className={classes.modal} open={linkConfirmModalOpen}>
          <div className={classes.paper}>
            <button
              className={classes.modalCloseButton}
              onClick={() => setLinkConfirmModalOpen(false)}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </button>
            <div className={classes.modalCheckCircle}>
              <img src={require("../../../images/nav-icons/sendrequest.png").default} alt="Send Request" style={{ width: "36px", height: "36px", objectFit: "contain" }} />
            </div>
            <div className={classes.modalTitle}>
              {enumStrings.perksLink.sendRequestLink}
            </div>
            <div className={classes.modalBody}>
              {enumStrings.perksLink.sendRequestConfirm}
              {" "}
              <span style={{ fontWeight: 700 }}>{requestedLinkChamberName}</span>?
            </div>
            <div className={classes.modalBody} style={{ marginTop: "0px" }}>
              {enumStrings.perksLink.clickSendRequest}
            </div>
            <Button
              className={classes.modalSendButton}
              onClick={() => {
                requestLink({
                  requestedLinkChamberIdManual: requestedLinkChamberId,
                });
              }}
              disabled={sendLoading}
            >
              {sendLoading ? (
                <CircularProgress style={{ color: "white" }} size={24} />
              ) : (
                <span className={classes.modalSendButtonText}>
                  {enumStrings.perksLink.sendRequest}
                </span>
              )}
            </Button>
          </div>
        </Modal>
      )}

      {requestSuccessModalOpen && (
        <Modal className={classes.modal} open={requestSuccessModalOpen}>
          <div className={classes.paper} style={{ padding: "40px 60px" }}>
            <button
              className={classes.modalCloseButton}
              onClick={() => setRequestSuccessModalOpen(false)}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </button>
            <div className={classes.modalCheckCircle}>
              <CheckIcon style={{ fontSize: "34px", color: "white" }} />
            </div>
            <div className={classes.modalTitle}>
              {enumStrings.perksLink.requestSentSuccessfully}
            </div>
          </div>
        </Modal>
      )}

      {cancelLinkRequestModalOpen && (
        <Modal className={classes.modal} open={cancelLinkRequestModalOpen}>
          <div className={classes.paper}>
            <button
              className={classes.modalCloseButton}
              onClick={() => setCancelLinkRequestModalOpen(false)}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </button>
            <div className={classes.modalCancelCircle}>
              <img src={require("../../../images/nav-icons/cancelrequest.png").default} alt="Cancel" style={{ width: "36px", height: "36px", objectFit: "contain" }} />
            </div>
            <div className={classes.modalTitle}>
              {enumStrings.chamberList.cancelPerksLinkRequest}
            </div>
            <div className={classes.modalBody}>
              {enumStrings.chamberList.cancelRequestConfirm}
              {" "}
              <span style={{ fontWeight: 700 }}>{requestedLinkChamberName}</span>?
            </div>
            <div className={classes.modalBody} style={{ marginTop: "0px" }}>
              {enumStrings.chamberList.clickCancelRequest}
            </div>
            <Button
              className={classes.modalCancelButton}
              onClick={() => {
                cancelRequest({
                  deletionReason: PerksLinkDeletionReasonEnum.Cancelled,
                });
              }}
              disabled={deleteRequestLoading}
            >
              {deleteRequestLoading ? (
                <CircularProgress style={{ color: "white" }} size={24} />
              ) : (
                <span className={classes.modalCancelButtonText}>
                  {enumStrings.chamberList.cancelRequestButton}
                </span>
              )}
            </Button>
          </div>
        </Modal>
      )}

      {cancelSuccessModalOpen && (
        <Modal className={classes.modal} open={cancelSuccessModalOpen}>
          <div className={classes.paper} style={{ padding: "40px 60px" }}>
            <button
              className={classes.modalCloseButton}
              onClick={() => setCancelSuccessModalOpen(false)}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </button>
            <div className={classes.modalCheckCircle}>
              <CheckIcon style={{ fontSize: "34px", color: "white" }} />
            </div>
            <div className={classes.modalTitle}>
              {enumStrings.chamberList.requestCancelledSuccessfully}
            </div>
          </div>
        </Modal>
      )}

      {denyLinkRequestModalOpen && (
        <Modal className={classes.modal} open={denyLinkRequestModalOpen}>
          <div
            className={classes.paper}
            style={{ justifyContent: "space-between", height: "430px" }}
          >
            <h1 style={{ fontWeight: "bold" }}>ç</h1>
            <div
              style={{
                padding: "50px",
                paddingBottom: 0,
                paddingTop: 20,
                textAlign: "left",
                fontSize: "17px",
                height: "100%",
                overflowY: "scroll",
              }}
            >
              {enumStrings.chamberList.cancelLinkRequestDescription2.replace(
                "{{requestedLinkChamberName}}",
                requestedLinkChamberName
              )}
            </div>
            <br />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                className={classes.modalButton}
                onClick={() => {
                  cancelRequest({
                    deletionReason: PerksLinkDeletionReasonEnum.Denied,
                  });
                }}
                variant="contained"
                disabled={deleteRequestLoading}
              >
                {deleteRequestLoading ? (
                  <CircularProgress style={{ color: "white" }} size={28} />
                ) : (
                  "Deny Request"
                )}
              </Button>
              <Button
                className={classes.modalButton}
                style={{
                  backgroundColor: "#37474f",
                  color: "#e9e9e9",
                }}
                onClick={() => setDenyLinkRequestModalOpen(false)}
                variant="contained"
                disabled={deleteRequestLoading}
              >
                {enumStrings.shopLocal.createShopLocalCampaign.close}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <ActionDialog
        open={unlinkRequestModalOpen}
        onClose={() => !deleteRequestLoading && setUnlinkRequestModalOpen(false)}
        icon={<img src={InformationIcon} alt="Information" style={{ width: 80, height: 80 }} />}
        iconBgColor="transparent"
        iconSize={80}
        title={enumStrings.chamberList.disablePerksLink}
        description={
          <>
            <p style={{ margin: "0 0 16px 0" }}>
              {enumStrings.chamberList.unLinkRequestDescription.replace(
                /\{\{requestedLinkChamberName\}\}/g,
                requestedLinkChamberName
              )}
            </p>
            <p style={{ margin: 0 }}>
              {enumStrings.chamberList.unLinkRequestDescription2.replace(
                /\{\{requestedLinkChamberName\}\}/g,
                requestedLinkChamberName
              )}
            </p>
          </>
        }
        buttonText={
          deleteRequestLoading ? undefined : enumStrings.perksLink.unlink
        }
        onButtonClick={() => {
          cancelRequest({
            deletionReason: PerksLinkDeletionReasonEnum.Unlinked,
          });
        }}
        secondaryButtonText={enumStrings.shopLocal.createShopLocalCampaign.close}
        onSecondaryButtonClick={() => setUnlinkRequestModalOpen(false)}
        maxWidth="580px"
      />

      {acceptRequestModalOpen && (
        <Modal className={classes.modal} open={acceptRequestModalOpen}>
          <div
            className={classes.paper}
            style={{ justifyContent: "space-between", height: "430px" }}
          >
            <h1 style={{ fontWeight: "bold" }}>
              {enumStrings.chamberList.acceptPerksLinkRequest}
            </h1>
            <div
              style={{
                padding: "50px",
                paddingBottom: 0,
                paddingTop: 20,
                textAlign: "left",
                fontSize: "17px",
                height: "100%",
                overflowY: "scroll",
              }}
            >
              {enumStrings.chamberList.acceptRequestDescription.replace(
                "{{requestedLinkChamberName}}",
                requestedLinkChamberName
              )}{" "}
              {requestedLinkChamberName}.
              <br />
              <br />
              {enumStrings.chamberList.acceptRequestDescription2.replace(
                "{{requestedLinkChamberName}}",
                requestedLinkChamberName
              )}{" "}
            </div>
            <br />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                className={classes.modalButton}
                onClick={acceptRequest}
                variant="contained"
                disabled={acceptRequestLoading}
              >
                {acceptRequestLoading ? (
                  <CircularProgress style={{ color: "white" }} size={28} />
                ) : (
                  "Accept Request"
                )}
              </Button>
              <Button
                className={classes.modalButton}
                style={{
                  backgroundColor: "#37474f",
                  color: "#e9e9e9",
                }}
                onClick={() => setAcceptRequestModalOpen(false)}
                variant="contained"
                disabled={acceptRequestLoading}
              >
                {enumStrings.shopLocal.createShopLocalCampaign.close}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <div style={{ width: "100%", alignSelf: "flex-start", paddingLeft: isMobile ? "12px" : "40px", paddingRight: isMobile ? "12px" : "40px" }}>
        <h3 style={{ fontFamily: "DM Sans, sans-serif", fontWeight: 700, fontSize: "20px", color: "#000000", marginBottom: "16px", marginTop: "10px" }}>
          {enumStrings.chamberList.chamberListTitle}
        </h3>
        <ol style={{ fontFamily: "DM Sans, sans-serif", fontSize: "14px", color: "#000000", lineHeight: "1.8", paddingLeft: "18px", margin: 0 }}>
          <li style={{ marginBottom: "8px" }}>
            <b>{enumStrings.chamberList.chamberListDescription.step1Title}</b>
            <br />
            {enumStrings.chamberList.chamberListDescription.step1Desc}
          </li>
          <li style={{ marginBottom: "8px" }}>
            <b>{enumStrings.chamberList.chamberListDescription.step2Title}</b>
            <br />
            {enumStrings.chamberList.chamberListDescription.step2Desc}
            <ul style={{ listStyleType: "disc", paddingLeft: "20px", margin: "4px 0 0 0" }}>
              <li>{enumStrings.chamberList.chamberListDescription.step2Bullet1}</li>
              <li>{enumStrings.chamberList.chamberListDescription.step2Bullet2}</li>
            </ul>
          </li>
          <li style={{ marginBottom: "8px" }}>
            <b>{enumStrings.chamberList.chamberListDescription.step3Title}</b>
            <br />
            {enumStrings.chamberList.chamberListDescription.step3Desc}
          </li>
          <li style={{ marginBottom: "8px" }}>
            <b>{enumStrings.chamberList.chamberListDescription.step4Title}</b>
            <br />
            {enumStrings.chamberList.chamberListDescription.step4Desc}
          </li>
          <li style={{ marginBottom: "4px" }}>
            <span dangerouslySetInnerHTML={{ __html: enumStrings.chamberList.chamberListDescription.step5 }} />
          </li>
        </ol>
      </div>

      <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", paddingLeft: isMobile ? "12px" : "40px", paddingRight: isMobile ? "12px" : "40px", marginTop: "20px", width: "100%", boxSizing: "border-box" }}>
        <div style={{ maxWidth: "700px", flexShrink: 0, width: isMobile ? "100%" : undefined }}>
          {customList}
        </div>
        {searchedItems.length > 8 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "6px 10px",
              backgroundColor: "#D9D9D9",
              borderRadius: "12px",
              marginLeft: "16px",
              whiteSpace: "nowrap",
            }}
          >
            <svg width="12" height="20" viewBox="0 0 16 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.41 0.59L8 5.17l4.59-4.58L14 2l-6 6-6-6 1.41-1.41z" fill="#000"/>
              <path d="M3.41 8.59L8 13.17l4.59-4.58L14 10l-6 6-6-6 1.41-1.41z" fill="#000"/>
              <path d="M3.41 16.59L8 21.17l4.59-4.58L14 18l-6 6-6-6 1.41-1.41z" fill="#000"/>
            </svg>
            <span
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#000000",
              }}
            >
              {enumStrings.perksLink.scrollToSeeMore || "Scroll to see more"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
